<template>
  <div ref="anchor" class="pycad-layout-anchor">
    <button
      ref="triggerRef"
      type="button"
      class="pycad-layout-trigger"
      :class="{ 'pycad-layout-trigger--open': open }"
      @click.stop="toggle"
    >
      <i class="mdi mdi-view-dashboard pycad-layout-trigger__icon"></i>
      <span class="pycad-layout-trigger__label">{{ activeLabel }}</span>
      <i
        class="mdi mdi-chevron-down pycad-layout-trigger__chev"
        :class="{ 'pycad-layout-trigger__chev--open': open }"
      ></i>
    </button>

    <Teleport to="body">
      <div
        v-if="open"
        ref="popoverRef"
        class="pycad-layout-popover"
        :style="menuStyle"
      >
        <button
          v-for="name in layoutNames"
          :key="name"
          type="button"
          class="pycad-layout-popover__item"
          :class="{
            'pycad-layout-popover__item--active': currentLayoutName === name,
          }"
          @click.stop="select(name)"
        >
          <i
            :class="['mdi', layoutIcon(name)]"
            class="pycad-layout-popover__icon"
          ></i>
          <span class="pycad-layout-popover__label">{{ name }}</span>
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { useViewStore } from '@/src/store/views';
import { useFixedDropdownMenu } from '@/src/composables/useFixedDropdownMenu';

const viewStore = useViewStore();
const { currentLayoutName, namedLayouts } = storeToRefs(viewStore);

const open = ref(false);
const anchor = ref<HTMLDivElement | null>(null);
const triggerRef = ref<HTMLElement>();
const popoverRef = ref<HTMLElement>();

const { menuStyle } = useFixedDropdownMenu(open, triggerRef);

onClickOutside(
  anchor,
  () => {
    open.value = false;
  },
  { ignore: [popoverRef] }
);

const LAYOUTS_IN_VIEW_MENU = new Set(['Oblique']);

const layoutNames = computed(() =>
  Object.keys(namedLayouts.value).filter(
    (name) => !LAYOUTS_IN_VIEW_MENU.has(name)
  )
);

const activeLabel = computed(() => currentLayoutName.value ?? 'Custom');

// Map well-known VolView layout names to MDI icons; fall back to a generic dashboard tile.
const LAYOUT_ICONS: Record<string, string> = {
  'Four Up': 'mdi-view-grid',
  'Axial Coronal Sagittal': 'mdi-view-quilt',
  'Axial Only': 'mdi-square-outline',
  '3D Only': 'mdi-cube-outline',
  Oblique: 'mdi-axis-arrow',
  '3D Primary': 'mdi-cube-scan',
};

function layoutIcon(name: string) {
  return LAYOUT_ICONS[name] ?? 'mdi-view-dashboard';
}

function toggle() {
  open.value = !open.value;
}

function select(name: string) {
  viewStore.switchToNamedLayout(name);
  open.value = false;
}
</script>

<style scoped>
.pycad-layout-anchor {
  position: relative;
  display: inline-flex;
}

.pycad-layout-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 12px;
  border-radius: var(--pycad-radius-sm, 6px);
  background: var(--pycad-elevated);
  border: 1px solid var(--pycad-border);
  color: var(--pycad-text-primary);
  font-size: 12px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  outline: none;
  white-space: nowrap;
  max-width: 210px;
}

.pycad-layout-trigger:hover {
  background: var(--pycad-hover);
  border-color: var(--pycad-border-strong);
}

.pycad-layout-trigger--open {
  background: var(--pycad-primary-dim);
  border-color: var(--pycad-primary-border);
  color: var(--pycad-primary);
}

.pycad-layout-trigger__icon {
  font-size: 16px;
  color: var(--pycad-primary);
  flex-shrink: 0;
}

.pycad-layout-trigger__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.pycad-layout-trigger__chev {
  font-size: 13px;
  color: var(--pycad-text-secondary);
  transition: transform 0.15s;
  flex-shrink: 0;
}
.pycad-layout-trigger__chev--open {
  transform: rotate(180deg);
}

.pycad-layout-popover {
  display: grid;
  grid-template-columns: repeat(3, minmax(104px, 1fr));
  gap: 5px;
  padding: 10px;
  background: var(--pycad-elevated);
  border: 1px solid var(--pycad-border);
  border-radius: var(--pycad-radius, 10px);
  box-shadow: var(--pycad-shadow-pop, 0 8px 28px rgba(0, 0, 0, 0.55));
}

.pycad-layout-popover__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 8px;
  border-radius: var(--pycad-radius-sm, 6px);
  border: 1px solid transparent;
  background: transparent;
  color: var(--pycad-text-secondary);
  font-size: 11px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
  outline: none;
  text-align: center;
  line-height: 1.3;
  white-space: normal;
}

.pycad-layout-popover__item:hover {
  background: var(--pycad-hover);
  color: var(--pycad-text-primary);
}

.pycad-layout-popover__item--active {
  background: var(--pycad-primary-dim);
  border-color: var(--pycad-primary-border);
  color: var(--pycad-primary);
}

.pycad-layout-popover__item--active:hover {
  background: rgba(255, 153, 0, 0.22);
  color: var(--pycad-primary);
}

.pycad-layout-popover__icon {
  font-size: 20px;
}
</style>
